import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Handshake } from "lucide-react";
import { eur } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function SupplierFields({ form, setForm }) {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");
  const [contacts, setContacts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [nc, setNc] = useState({ name: "", phone: "", email: "" });

  useEffect(() => { api.get("/contacts").then(({ data }) => setContacts(data || [])).catch(() => {}); }, []);

  const sorted = [...contacts].sort((a, b) => (b.type === "fournisseur") - (a.type === "fournisseur") || (a.name || "").localeCompare(b.name || ""));
  const create = async () => {
    if (!nc.name.trim()) return toast.error("Nom du prestataire requis");
    try {
      const { data } = await api.post("/contacts", { ...nc, name: nc.name.trim(), type: "fournisseur", kind: "pro" });
      setContacts((c) => [...c, data]); setForm((f) => ({ ...f, supplier_id: data.id })); setCreating(false); setNc({ name: "", phone: "", email: "" });
      toast.success("Prestataire ajouté aux contacts");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };
  const margin = (parseFloat(form.price) || 0) - (parseFloat(form.supplier_cost) || 0);

  return (
    <div data-testid="supplier-fields" className="space-y-3 rounded-xl border border-[#FFD500]/25 bg-[#FFD500]/5 p-3">
      <p className="flex items-center gap-1.5 text-xs font-medium text-[#E6B800]"><Handshake className="h-3.5 w-3.5" />Interne uniquement — jamais affiché au client</p>
      <div>
        <Label className="text-zinc-400">Prestataire (loué chez)</Label>
        {!creating ? (
          <div className="mt-1 flex gap-2">
            <Select value={form.supplier_id || ""} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
              <SelectTrigger data-testid="item-supplier" className="flex-1 border-zinc-800 bg-zinc-900"><SelectValue placeholder="Choisir dans mes contacts" /></SelectTrigger>
              <SelectContent className="max-h-72 border-zinc-800 bg-zinc-900">
                {sorted.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}{c.type === "fournisseur" ? " · fournisseur" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="supplier-new-btn" type="button" variant="outline" onClick={() => setCreating(true)} className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800"><Plus className="h-4 w-4" /></Button>
          </div>
        ) : (
          <div className="mt-1 space-y-2 rounded-lg border border-zinc-800 bg-zinc-900 p-2">
            <Input data-testid="supplier-new-name" placeholder="Nom du prestataire" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} className="border-zinc-800 bg-zinc-950" />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="supplier-new-phone" placeholder="Téléphone" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} className="border-zinc-800 bg-zinc-950" />
              <Input data-testid="supplier-new-email" placeholder="Email" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} className="border-zinc-800 bg-zinc-950" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setCreating(false)} className="text-zinc-400">Annuler</Button>
              <Button data-testid="supplier-new-save" type="button" onClick={create} className="rounded-full bg-[#FF6A00] text-white hover:bg-[#E65F00]">Créer le contact</Button>
            </div>
          </div>
        )}
      </div>
      {isAdmin && (
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-zinc-400">Coût prestataire € <span className="text-zinc-600">/ unité</span></Label><Input data-testid="item-supplier-cost" type="number" className="mt-1 border-zinc-800 bg-zinc-900" value={form.supplier_cost} onChange={(e) => setForm({ ...form, supplier_cost: e.target.value })} /></div>
        <div><Label className="text-zinc-400">Marge / unité</Label><p data-testid="item-supplier-margin" className={`mt-1 flex h-10 items-center rounded-md border border-zinc-800 bg-zinc-900 px-3 text-sm font-semibold ${margin < 0 ? "text-red-400" : "text-emerald-400"}`}>{eur(margin)}</p></div>
      </div>
      )}
    </div>
  );
}
